import { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Card,
  Form,
  Row,
  Col,
  Button,
  Stack,
  Alert,
  Container,
} from "react-bootstrap";
import QuestionaryService from "../../services/questionary.service";
import { QuestionaryQuestion } from "../../models/QuestionaryQuestion";
import { Questionary } from "../../models/Questionary";
import { QuestionarySection } from "../../models/QuestionarySection";
import { DisplayQuestionaryQuestions } from "../DisplayQuestionaryQuestions";
import FileUpload from "../FillQuestionary/FileUpload";
import { useLoading } from "../App/LoadingContext";
import validation from "../../Helpers/utils/validation";
import { useNavigate } from "react-router-dom";
import { QuestionaryDetail } from "../../models/QuestionaryDetail";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretLeft,
  faCaretRight,
  faSave,
  faClipboardCheck,
} from "@fortawesome/free-solid-svg-icons";

const FillQuestionary = () => {
  const [questionary, setQuestionary] = useState<Questionary | null>(null);
  const [sections, setSections] = useState<QuestionarySection[]>([]);
  const [localQuestions, setLocalQuestions] = useState<QuestionaryQuestion[]>(
    []
  );
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<number>>(
    new Set()
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isNeededReloadFiles, setIsNeededReloadFiles] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");

  const { wrapWithLoading } = useLoading();
  const navigate = useNavigate();
  const questionaryId = parseInt(localStorage.getItem("questionaryId") || "-1");

  useEffect(() => {
    const loadAllData = async () => {
      if (questionaryId === -1) return;

      const qData = await QuestionaryService.getQuestionaryById(questionaryId);
      setQuestionary(qData);

      const secData = await QuestionaryService.getSections(
        qData.questionaryTypeId
      );
      const sortedSections = secData
        .sort((a, b) => a.position - b.position)
        .map((s) => ({
          ...s,
          questionarySubsections:
            s.questionarySubsections?.sort((a, b) => a.position - b.position) ??
            [],
        }));
      setSections(sortedSections);

      const allSubsections = sortedSections.flatMap(
        (s) => s.questionarySubsections ?? []
      );
      const questionsPromises = allSubsections.map((sub) =>
        QuestionaryService.getQuestions(sub.id, questionaryId)
      );

      const questionsBySubsection = await Promise.all(questionsPromises);
      const allQuestionsRaw = questionsBySubsection.flat();

      const details = await QuestionaryService.getQuestionaryDetail(
        questionaryId
      );
      const allQuestionsWithDetails = allQuestionsRaw.map((q) => {
        const questionDetails = details.filter((d) => d.questionaryQuestionId === q.id);
        if (questionDetails.length === 0) return q;

        const updatedQuestion = { ...q };

        if ([1, 5, 7, 12, 13].includes(q.questionTypeId)) {
          // Tipos de respuesta única con texto
          const detail = questionDetails[0];
          updatedQuestion.answerText = detail.answerText;
        } else if (q.questionTypeId === 2) {
          // Tipo 2: Selección única (radio button)
          const detail = questionDetails[0];
          updatedQuestion.questionaryAnswers = q.questionaryAnswers?.map(
            (ans) =>
              ans.id === detail.questionaryAnswerId
                ? { ...ans, isChecked: true }
                : { ...ans, isChecked: false }
          );
        } else if (q.questionTypeId === 3) {
          // Tipo 3: Selección múltiple (checkbox) - puede tener múltiples details
          const checkedAnswerIds = new Set(questionDetails.map(d => d.questionaryAnswerId));
          updatedQuestion.questionaryAnswers = q.questionaryAnswers?.map(
            (ans) => ({
              ...ans,
              isChecked: checkedAnswerIds.has(ans.id)
            })
          );
        } else if ([4, 8].includes(q.questionTypeId)) {
          const detail = questionDetails[0];
          updatedQuestion.answerText = detail.answerText;
          updatedQuestion.questionaryAnswers = q.questionaryAnswers?.map(
            (ans) => {
              if (ans.id === detail.questionaryAnswerId) {
                return {
                  ...ans,
                  answerText: detail.answerText,
                };
              }
              return ans;
            }
          );
        }
        return updatedQuestion;
      });

      setLocalQuestions(allQuestionsWithDetails);
    };

    wrapWithLoading(loadAllData)();
  }, [questionaryId]);

  const handleSetQuestions = (newQuestions: QuestionaryQuestion[]) => {
    setLocalQuestions(newQuestions);
  };

  const handleSaveButton = wrapWithLoading(async () => {
    // Resetea el estado de validación al iniciar el guardado.
    setShowValidationError(false);
    setInvalidQuestionIds(new Set());

    setIsSaving(true);
    try {
      const detailsToSave: QuestionaryDetail[] = [];

      localQuestions.forEach((q) => {
        const questionHasTextAnswer =
          q.answerText &&
          q.answerText.trim() !== "" &&
          q.answerText.trim() !== "[]";
        const questionHasCheckedAnswer = q.questionaryAnswers?.some(
          (a) => a.isChecked
        );

        if (questionHasTextAnswer) {
          const answer = q.questionaryAnswers?.[0]; // Para tipos Matriz, Texto, etc., que usan el primer answer
          if (answer) {
            detailsToSave.push({
              id: answer.questionaryDetailId || 0,
              questionaryId: questionaryId,
              questionaryQuestionId: q.id,
              answerText: q.answerText,
              questionaryAnswerId: answer.id,
              comment: answer.comment,
            });
          }
        } else if (questionHasCheckedAnswer) {
          q.questionaryAnswers
            ?.filter((a) => a.isChecked)
            .forEach((answer) => {
              detailsToSave.push({
                id: answer.questionaryDetailId || 0,
                questionaryId: questionaryId,
                questionaryQuestionId: q.id,
                answerText: undefined, // Tipos de selección no usan answerText
                questionaryAnswerId: answer.id,
                comment: answer.comment,
              });
            });
        }
      });

      await QuestionaryService.saveQuestionaryDetail(detailsToSave);
      setShowSavedMessage(true);
    } catch (error) {
      setErrorTitle("Error al Guardar");
      setErrorMessage("No se pudo guardar el progreso del cuestionario.");
      setShowValidationError(true);
    } finally {
      setIsSaving(false);
    }
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const failedIds = await validation.getInvalidQuestionIds(
      localQuestions,
      questionaryId
    );
    setInvalidQuestionIds(new Set(failedIds));

    if (failedIds.length > 0) {
      setErrorTitle("Error de Validación");
      setErrorMessage(
        "Debe contestar las preguntas requeridas para poder continuar"
      );
      setShowValidationError(true);
      window.scrollTo(0, 0);
      return;
    }

    setShowValidationError(false);
    await handleSaveButton();
    navigate("/QuestionarySummary", { state: { questionaryId } });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (
      event.key === "Enter" &&
      (event.target as HTMLElement).tagName.toLowerCase() === "input"
    ) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (showValidationError || showSavedMessage) {
      const timer = setTimeout(() => {
        setShowValidationError(false);
        setShowSavedMessage(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showValidationError, showSavedMessage]);

  return (
    <div>
      <style type="text/css">{`
                .custom-tabs-container .nav-tabs { border-bottom-color: #dee2e6; }
                .custom-tabs-container .nav-tabs .nav-link { font-size: 1.05rem; color: #495057; background-color: #f8f9fa; border-color: #dee2e6 #dee2e6 #dee2e6; margin-right: 2px; border-top-left-radius: 0.375rem; border-top-right-radius: 0.375rem; transition: all 0.2s ease-in-out; }
                .custom-tabs-container .nav-tabs .nav-link:hover:not(.active) { border-color: #0d6efd; color: #0d6efd; }
                .custom-tabs-container .nav-tabs .nav-link.active { color: #fff !important; background-color: #0d6efd; border-color: #0d6efd #0d6efd #fff; font-weight: bold; }
            `}</style>
      <div className="notification-container">
        {showValidationError && (
          <Alert
            variant="danger"
            onClose={() => setShowValidationError(false)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{errorTitle}</Alert.Heading>
            <p>{errorMessage}</p>
          </Alert>
        )}
        {showSavedMessage && (
          <Alert
            variant="success"
            onClose={() => setShowSavedMessage(false)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>Éxito</Alert.Heading>
            <p>El cuestionario ha sido guardado.</p>
          </Alert>
        )}
      </div>

      <Container className="pt-3">
        <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown} noValidate>
          <Card>
            <Card.Header className="card-header-main bg-primary text-white">
              <Row>
                <Col className="text-end">Nombre: {questionary?.name}</Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <div className="custom-tabs-container">
                <Tabs
                  activeKey={currentTab}
                  onSelect={(k) => setCurrentTab(k ? parseInt(k) : 0)}
                  id="tabSections"
                  className="mb-3"
                >
                  {sections.map((section, index) => (
                    <Tab eventKey={index} title={section.name} key={section.id}>
                      {section.questionarySubsections?.map((subsection) => (
                        <Card key={subsection.id} className="mb-3">
                          <Card.Header className="bg-primary text-white">
                            {subsection.name}
                          </Card.Header>
                          <Card.Body>
                            <DisplayQuestionaryQuestions
                              SubsectionID={subsection.id}
                              questions={localQuestions}
                              setQuestions={handleSetQuestions}
                              invalidIds={invalidQuestionIds}
                              QuestionaryId={questionaryId}
                              onHandleReloadFiles={() =>
                                setIsNeededReloadFiles((p) => !p)
                              }
                              isNeededReloadFiles={isNeededReloadFiles}
                            />
                          </Card.Body>
                        </Card>
                      ))}
                    </Tab>
                  ))}
                  <Tab
                    eventKey={sections.length}
                    title="Archivos"
                    key="files-tab"
                  >
                    <Card>
                      <Card.Body>
                        <FileUpload
                          questionaryId={questionaryId}
                          onUploadSuccess={() =>
                            console.log("Upload successful")
                          }
                          isNeededReloadFiles={isNeededReloadFiles}
                          onHandleReloadFiles={() =>
                            setIsNeededReloadFiles((p) => !p)
                          }
                        />
                      </Card.Body>
                    </Card>
                  </Tab>
                </Tabs>
              </div>
              <div style={{ height: "5rem" }} />
            </Card.Body>
            <Card.Footer className="text-center fixed-bottom bg-light border-top py-3">
              <Stack
                direction="horizontal"
                gap={3}
                className="justify-content-center"
              >
                <Button
                  variant="secondary"
                  disabled={currentTab === 0}
                  onClick={() => setCurrentTab((prev) => prev - 1)}
                >
                  <a>
                    <FontAwesomeIcon
                      icon={faCaretLeft}
                      style={{
                        paddingRight: "3px",
                      }}
                    />
                    Anterior
                  </a>
                </Button>
                <Button
                  variant="secondary"
                  disabled={currentTab >= sections.length}
                  onClick={() => setCurrentTab((prev) => prev + 1)}
                >
                  <a>
                    Siguiente
                    <FontAwesomeIcon
                      icon={faCaretRight}
                      style={{
                        paddingLeft: "3px",
                      }}
                    />
                  </a>
                </Button>
                <div className="vr" />
                <Button
                  variant="primary"
                  onClick={handleSaveButton}
                  disabled={isSaving}
                >
                  <a>
                    <FontAwesomeIcon
                      icon={faSave}
                      style={{
                        paddingRight: "3px",
                      }}
                    />
                    {isSaving ? "Guardando..." : "Guardar y continuar editando"}
                  </a>
                </Button>
                <Button type="submit" variant="success">
                  <a>
                    <FontAwesomeIcon
                      icon={faClipboardCheck}
                      style={{
                        paddingRight: "3px",
                      }}
                    />
                    Completar cuestionario
                  </a>
                </Button>
              </Stack>
            </Card.Footer>
          </Card>
        </Form>
      </Container>
    </div>
  );
};

export default FillQuestionary;
